import React, { useEffect, useRef, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonFooter,
  IonItem,
  IonLabel,
  IonInput,
  IonAlert,
  IonSpinner,
  IonIcon,
  IonText,
} from '@ionic/react';
import { pencilOutline, closeOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
}

interface FormData {
  currentPassword: string;
  newPassword: string;
  reenterPassword: string;
}

type Mode = 'view' | 'edit';

const EMPTY_FORM: FormData = { currentPassword: '', newPassword: '', reenterPassword: '' };

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onDidDismiss }) => {
  const [mode, setMode] = useState<Mode>('view');
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [showExitAlert, setShowExitAlert] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const forceClosingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      forceClosingRef.current = false;
      setMode('view');
      setFormData(EMPTY_FORM);
      setErrors({});
    }
  }, [isOpen]);

  const hasUnsavedChanges = (): boolean =>
    formData.currentPassword !== '' || formData.newPassword !== '' || formData.reenterPassword !== '';

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.currentPassword.trim()) newErrors.currentPassword = 'Current password is required';
    if (!formData.newPassword.trim()) newErrors.newPassword = 'New password is required';
    if (!formData.reenterPassword.trim()) {
      newErrors.reenterPassword = 'Please re-enter your new password';
    } else if (formData.newPassword !== formData.reenterPassword) {
      newErrors.reenterPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (forceClosingRef.current) return;
    if (mode === 'edit' && hasUnsavedChanges()) {
      setShowExitAlert(true);
    } else {
      onDidDismiss();
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setShowCancelAlert(true);
    } else {
      setFormData(EMPTY_FORM);
      setErrors({});
      setMode('view');
    }
  };

  const handleDiscardToView = () => {
    setShowCancelAlert(false);
    setFormData(EMPTY_FORM);
    setErrors({});
    setMode('view');
  };

  const handleForceClose = () => {
    setShowExitAlert(false);
    forceClosingRef.current = true;
    onDidDismiss();
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await apiService.changeAccountPassword(formData.currentPassword, formData.newPassword);
      setToastMessage('Password changed successfully');
      setToastVariant('success');
      setShowToast(true);
      setFormData(EMPTY_FORM);
      setMode('view');
    } catch {
      setToastMessage('Failed to change password. Please check your current password and try again.');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Change Password</IonTitle>
            <IonButtons slot="end">
              {mode === 'view' && (
                <IonButton onClick={() => setMode('edit')} aria-label="Edit">
                  <IonIcon slot="icon-only" icon={pencilOutline} />
                </IonButton>
              )}
              <IonButton onClick={handleClose} disabled={isSaving} aria-label="Close">
                <IonIcon slot="icon-only" icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {mode === 'view' ? (
            <div style={{ padding: '24px 16px', textAlign: 'center' }}>
              <IonText color="medium">
                <p style={{ fontSize: '0.95rem' }}>
                  Click the edit icon to change your account password.
                </p>
              </IonText>
            </div>
          ) : (
            <div style={{ padding: '16px' }}>
              <IonItem lines="full">
                <IonLabel position="stacked">Current Password</IonLabel>
                <IonInput
                  type="password"
                  value={formData.currentPassword}
                  onIonInput={(e) => updateField('currentPassword', e.detail.value ?? '')}
                  disabled={isSaving}
                />
              </IonItem>
              {errors.currentPassword && (
                <p style={{ color: 'var(--ion-color-danger)', fontSize: '0.85rem', margin: '4px 16px 0' }}>
                  {errors.currentPassword}
                </p>
              )}

              <IonItem lines="full" style={{ marginTop: '8px' }}>
                <IonLabel position="stacked">New Password</IonLabel>
                <IonInput
                  type="password"
                  value={formData.newPassword}
                  onIonInput={(e) => updateField('newPassword', e.detail.value ?? '')}
                  disabled={isSaving}
                />
              </IonItem>
              {errors.newPassword && (
                <p style={{ color: 'var(--ion-color-danger)', fontSize: '0.85rem', margin: '4px 16px 0' }}>
                  {errors.newPassword}
                </p>
              )}

              <IonItem lines="full" style={{ marginTop: '8px' }}>
                <IonLabel position="stacked">Re-enter New Password</IonLabel>
                <IonInput
                  type="password"
                  value={formData.reenterPassword}
                  onIonInput={(e) => updateField('reenterPassword', e.detail.value ?? '')}
                  disabled={isSaving}
                />
              </IonItem>
              {errors.reenterPassword && (
                <p style={{ color: 'var(--ion-color-danger)', fontSize: '0.85rem', margin: '4px 16px 0' }}>
                  {errors.reenterPassword}
                </p>
              )}
            </div>
          )}
        </IonContent>

        {mode === 'edit' && (
          <IonFooter>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={handleCancel} disabled={isSaving}>Cancel</IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton
                  strong
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges()}
                >
                  {isSaving
                    ? <IonSpinner name="crescent" style={{ width: '20px', height: '20px' }} />
                    : 'Save'}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonFooter>
        )}
      </IonModal>

      <IonAlert
        isOpen={showCancelAlert}
        onDidDismiss={() => setShowCancelAlert(false)}
        header="Discard Changes"
        message="Are you sure you want to discard your changes?"
        buttons={[
          { text: 'Keep Editing', role: 'cancel', handler: () => setShowCancelAlert(false) },
          { text: 'Discard', role: 'destructive', handler: handleDiscardToView },
        ]}
      />

      <IonAlert
        isOpen={showExitAlert}
        onDidDismiss={() => setShowExitAlert(false)}
        header="Unsaved Changes"
        message="You have unsaved changes. Close anyway?"
        buttons={[
          { text: 'Stay', role: 'cancel', handler: () => setShowExitAlert(false) },
          { text: 'Close', role: 'destructive', handler: handleForceClose },
        ]}
      />

      <Toaster
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
    </>
  );
};

export default ChangePasswordModal;
